<template>
  <div
    class="h-100 d-flex flex-column"
    style="overflow: hidden"
  >
    <div class="d-flex justify-content-between align-items-center">
      <div class="page-header">
        <h1>
          {{ $t('shop.moveToListingHeader') }}
        </h1>
      </div>
      <div>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="cancel"
        >
          ×
        </button>
      </div>
    </div>

    <hr class="mt-1">

    <div class="d-flex justify-content-end mb-4">
      <button
        class="btn btn-primary btn-lg next-btn mr-1"
        :disabled="pending || !targetListingId || checkedIds.length === 0"
        @click="confirm"
      >
        {{ $t('shop.moveToListing') }}
        <i class="fas fa-check px-2" />
      </button>
    </div>

    <div
      class="row flex-grow-1"
      style="overflow: hidden"
    >
      <div
        class="col-7 h-100"
        style="overflow-y: auto"
      >
        <div class="d-flex mb-3 mt-1">
          <PayersGroupFilterDropdown
            :selected.sync="groupFilter"
            class="mr-3"
            hide-edit
          />
          <SearchBox
            v-model="search"
            class="mr-3"
          />
        </div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="font-weight-bold">
            {{ $t('shop.subscriptions') }}
          </div>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="toggleAll"
          >
            {{ allChecked ? $t('general.unselectAll') : $t('general.selectAll') }}
          </button>
        </div>
        <div
          v-for="row in filteredRows"
          :key="row.id"
          class="d-flex align-items-center py-1 subscription-row"
          @click="toggleRow(row.id)"
        >
          <Checkbox
            :value="!!checked[row.id]"
            :emit="false"
          />
          <PayerName
            v-bind="row.payer"
            class="ml-2 flex-grow-1"
            no-code
          />
          <div class="text-secondary small mr-2">
            {{ row.listingName }}
          </div>
          <span
            v-if="row.mismatch"
            v-tippy
            :content="$t('shop.moveToListingFormMismatchInfo')"
            class="badge badge-danger ml-2"
          >
            {{ $t('shop.moveToListingFormMismatch') }}
          </span>
        </div>
      </div>
      <div
        class="col-5 h-100"
        style="overflow-y: auto"
      >
        <div class="font-weight-bold mb-2">
          {{ $t('shop.moveToListingTarget') }}
        </div>
        <ProductList
          subscriptions
          only-listings
          single-select
          :catalog-id="catalogId"
          :selected="[]"
          :selected-listings.sync="targetListingIds"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PayerName from '@/components/payers/PayerName';
import PayersGroupFilterDropdown from '@/components/payers/PayersGroupFilterDropdown';
import Checkbox from '@/components/utils/Checkbox';
import createSearch from '@/utils/search';
import { mapActions, mapGetters } from 'vuex';
import ProductList from './ProductList';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    subscriptions: [],
    payersById: {},
    search: '',
    groupFilter: '#all',
    targetListingIds: [],
    targetLayout: null,
    layoutFetched: false,
    checked: {},
    pending: false,
  }),
  components: {
    PayerName,
    PayersGroupFilterDropdown,
    Checkbox,
    ProductList,
  },
  computed: {
    ...mapGetters(['selectedSubscriptions', 'groups']),
    ...mapGetters('shop', ['catalogDetails']),
    targetListingId() {
      return this.targetListingIds[0] || null;
    },
    rows() {
      return this.subscriptions.map((row) => ({
        ...row,
        payer: this.payersById[row.payerId] || row.payer,
        mismatch: !!this.targetListingId
          && this.layoutFetched
          && (row.formLayout || null) !== (this.targetLayout || null),
      }));
    },
    filteredRows() {
      const search = createSearch(this.search);

      return this.rows
        .filter((row) => this.groupFilter === '#all' || (row.payer?.groups || []).includes(this.groupFilter))
        .filter((row) => this.search.length === 0
          || search(row.payer?.name || '')
          || search(row.listingName || ''));
    },
    checkedIds() {
      return Object.keys(this.checked).filter((id) => this.checked[id]);
    },
    allChecked() {
      if (this.filteredRows.length === 0) {
        return false;
      }

      const payerIds = [...new Set(this.filteredRows.map((r) => r.payerId))];
      return payerIds.every((payerId) => this.filteredRows
        .some((r) => r.payerId === payerId && this.checked[r.id]));
    },
  },
  watch: {
    targetListingId(id) {
      this.checked = {};
      this.targetLayout = null;
      this.layoutFetched = false;

      if (!id) {
        return;
      }

      this.getListingFormLayout({
        params: {
          query: {
            catalogId: this.catalogId,
            listingId: id,
          },
        },
      }).then(({ data }) => {
        this.targetLayout = data.layout;
        this.layoutFetched = true;

        this.checked = this.pickOnePerPayer(this.rows.filter((row) => !row.mismatch));
      });
    },
  },
  methods: {
    ...mapActions(['getPayersByIds']),
    ...mapActions('shop', [
      'getListingFormLayout',
      'moveSubscriptionsToListing',
      'getCatalogListings',
      'getShopCatalog',
    ]),
    requestPayers() {
      const payerIds = [...new Set(this.subscriptions.map((row) => row.payerId))];
      if (payerIds.length === 0) {
        return;
      }

      const withGroup = this.groups
        .map((g) => g.code)
        .filter((code) => code !== '#all');

      this.getPayersByIds({
        params: {
          query: {
            ids: payerIds,
            withGroup,
          },
        },
      }).then(({ data }) => {
        this.payersById = data.reduce((acc, payer) => {
          acc[payer.id] = payer;
          return acc;
        }, {});
      });
    },
    pickOnePerPayer(candidateRows) {
      const checked = {};
      const seenPayers = new Set();

      candidateRows.forEach((row) => {
        if (!seenPayers.has(row.payerId)) {
          checked[row.id] = true;
          seenPayers.add(row.payerId);
        }
      });

      return checked;
    },
    toggleRow(id) {
      if (this.checked[id]) {
        this.$delete(this.checked, id);
        return;
      }

      const row = this.rows.find((r) => r.id === id);
      const samePayerRow = this.rows.find((r) => this.checked[r.id] && r.payerId === row.payerId);
      if (samePayerRow) {
        this.$delete(this.checked, samePayerRow.id);
      }

      this.$set(this.checked, id, true);
    },
    toggleAll() {
      const value = !this.allChecked;

      if (!value) {
        this.filteredRows.forEach((row) => {
          this.$delete(this.checked, row.id);
        });
        return;
      }

      const seenPayers = new Set(
        this.rows
          .filter((row) => this.checked[row.id])
          .map((row) => row.payerId),
      );

      this.filteredRows.forEach((row) => {
        if (this.checked[row.id] || seenPayers.has(row.payerId)) {
          return;
        }

        this.$set(this.checked, row.id, true);
        seenPayers.add(row.payerId);
      });
    },
    confirm() {
      this.pending = true;

      this.moveSubscriptionsToListing({
        params: {
          query: {
            catalogId: this.catalogId,
          },
        },
        data: {
          targetListingId: this.targetListingId,
          items: this.checkedIds
            .map((id) => this.rows.find((r) => r.id === id))
            .map((r) => ({ payerId: r.payerId, listingId: r.listingId })),
        },
      })
        .then(() => {
          this.$router.back();
        })
        .finally(() => {
          this.pending = false;
        });
    },
    cancel() {
      this.$router.back();
    },
  },
  created() {
    if (this.$route.query.useSelectedSubscriptions) {
      this.subscriptions = this.selectedSubscriptions;
    }

    this.requestPayers();

    this.getCatalogListings({
      params: {
        catalogId: this.catalogId,
      },
    });

    if (!this.catalogDetails) {
      this.getShopCatalog({
        params: {
          catalogId: this.catalogId,
        },
      });
    }

    this.$emit('page', 'form');
  },
};
</script>

<style lang="scss" scoped>
  .close {
    font-size: 40px;
  }

  .subscription-row {
    cursor: pointer;
    border-bottom: 1px solid #eee;

    &:hover {
      background-color: rgba(100, 100, 100, 0.05);
    }
  }
</style>
